import React, { useCallback, useEffect, useState } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  RefreshCw,
} from 'lucide-react'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { PageHeader } from '@/components/layout/PageHeader'
import { KpiCard } from '@/components/layout/KpiCard'
import { Button } from '@/components/ui/Button'
import { DateInput } from '@/components/forms/DateInput'
import { formatDateFull } from '@/lib/dateFormat'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { EmptyState } from '@/components/ui/EmptyState'
import { PageError, PageLoading } from '@/components/ui/PageAsyncState'
import {
  fetchProductionOutputReportApi,
  type GroupByPeriod,
  type OutputReportView,
  type ProductionOutputReportData,
  type StageName,
} from '@/services/reports/productionOutputApi'
import { exportProductionOutputToExcel } from './productionOutputExcel'

function getTodayVN(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Ho_Chi_Minh' }).format(new Date())
}

function getStartOfMonthVN(): string {
  const t = getTodayVN()
  return `${t.slice(0, 7)}-01`
}

function getSevenDaysAgoVN(): string {
  const d = new Date()
  d.setDate(d.getDate() - 6)
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Ho_Chi_Minh' }).format(d)
}

function formatNumber(num: number | null | undefined): string {
  if (num == null) return '0'
  return num.toLocaleString('vi-VN')
}

export interface ProductionOutputReportPageProps {
  initialData?: ProductionOutputReportData
}

export function ProductionOutputReportPage({
  initialData,
}: ProductionOutputReportPageProps = {}): React.ReactElement {
  const [data, setData] = useState<ProductionOutputReportData | null>(initialData ?? null)
  const [loading, setLoading] = useState(!initialData)
  const [error, setError] = useState<string | null>(null)

  // Filters state
  const [activeView, setActiveView] = useState<OutputReportView>(initialData?.filters.view ?? 'by_period')
  const [from, setFrom] = useState(initialData?.filters.from ?? getStartOfMonthVN())
  const [to, setTo] = useState(initialData?.filters.to ?? getTodayVN())
  const [groupBy, setGroupBy] = useState<GroupByPeriod>(initialData?.filters.group_by ?? 'day')
  const [selectedPlanLineId, setSelectedPlanLineId] = useState(initialData?.filters.plan_line_id ?? '')
  const [productCodeInput, setProductCodeInput] = useState(initialData?.filters.product_code ?? '')
  const [selectedStage, setSelectedStage] = useState<StageName | ''>(initialData?.filters.stage ?? '')
  const [page, setPage] = useState(initialData?.pagination.page ?? 1)
  const limit = 30

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetchProductionOutputReportApi({
        from,
        to,
        group_by: groupBy,
        plan_line_id: selectedPlanLineId || undefined,
        product_code: productCodeInput || undefined,
        stage: selectedStage || undefined,
        view: activeView,
        page,
        limit,
      })
      setData(res)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải báo cáo sản lượng')
    } finally {
      setLoading(false)
    }
  }, [from, to, groupBy, selectedPlanLineId, productCodeInput, selectedStage, activeView, page])

  useEffect(() => {
    if (initialData && !selectedPlanLineId && !productCodeInput && !selectedStage && page === 1) return
    void loadData()
  }, [loadData, initialData, selectedPlanLineId, productCodeInput, selectedStage, page])

  // Quick filter handlers
  const handleFilterToday = () => {
    const t = getTodayVN()
    setFrom(t)
    setTo(t)
    setPage(1)
  }

  const handleFilter7Days = () => {
    setFrom(getSevenDaysAgoVN())
    setTo(getTodayVN())
    setPage(1)
  }

  const handleFilterThisMonth = () => {
    setFrom(getStartOfMonthVN())
    setTo(getTodayVN())
    setPage(1)
  }

  const handleExportExcel = () => {
    if (!data) return
    exportProductionOutputToExcel(data, activeView)
  }

  // Determine if empty
  const isPeriodEmpty = !data || data.by_period.length === 0
  const isPartsEmpty = !data || !data.order_parts || data.order_parts.parts.length === 0
  const isCumulativeEmpty = !data || data.order_cumulative.length === 0

  const isCurrentViewEmpty =
    activeView === 'by_period'
      ? isPeriodEmpty
      : activeView === 'order_parts'
      ? isPartsEmpty
      : isCumulativeEmpty

  return (
    <div className="space-y-4">
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Báo cáo', to: '/reports' },
          { label: 'Sản lượng 7 công đoạn' },
        ]}
      />

      <PageHeader
        title="Báo cáo Sản lượng 7 công đoạn"
        subtitle="Theo dõi sản lượng thực hiện theo ngày, tuần, tháng cho 7 công đoạn sản xuất."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void loadData()}
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Làm mới
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleExportExcel}
              disabled={loading || !data}
              data-testid="export-excel-btn"
            >
              <Download className="mr-1.5 h-4 w-4" />
              Xuất Excel (.xlsx)
            </Button>
          </div>
        }
      />

      {/* KPI Cards Summary */}
      {data && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            label="Phôi (chi tiết)"
            value={formatNumber(data.summary.total_phoi)}
            hint="Tổng công đoạn Phôi trong kỳ"
            tone="default"
          />
          <KpiCard
            label="Định hình (chi tiết)"
            value={formatNumber(data.summary.total_dinh_hinh)}
            hint="Tổng công đoạn Định hình trong kỳ"
            tone="default"
          />
          <KpiCard
            label="Lắp ráp (SP)"
            value={formatNumber(data.summary.total_lap_rap)}
            hint="Tổng công đoạn Lắp ráp trong kỳ"
            tone="default"
          />
          <KpiCard
            label="Đóng gói (SP)"
            value={formatNumber(data.summary.total_dong_goi)}
            hint="Tổng công đoạn Đóng gói trong kỳ"
            tone="success"
          />
        </div>
      )}

      {/* Filter Bar */}
      <div className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)] space-y-3">
        {/* Row 1: View selector tabs + Date Quick Filters */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] pb-3">
          {/* 3 View Tabs */}
          <div className="flex flex-wrap items-center gap-1.5" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeView === 'by_period'}
              data-testid="tab-by-period"
              onClick={() => {
                setActiveView('by_period')
                setPage(1)
              }}
              className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                activeView === 'by_period'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-[var(--color-surface-hover)] text-[var(--color-text)] hover:bg-[var(--color-border)]'
              }`}
            >
              Theo mã hàng × công đoạn
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeView === 'order_parts'}
              data-testid="tab-order-parts"
              onClick={() => {
                setActiveView('order_parts')
                setPage(1)
              }}
              className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                activeView === 'order_parts'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-[var(--color-surface-hover)] text-[var(--color-text)] hover:bg-[var(--color-border)]'
              }`}
            >
              Theo chi tiết của một lệnh
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeView === 'order_cumulative'}
              data-testid="tab-order-cumulative"
              onClick={() => {
                setActiveView('order_cumulative')
                setPage(1)
              }}
              className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                activeView === 'order_cumulative'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-[var(--color-surface-hover)] text-[var(--color-text)] hover:bg-[var(--color-border)]'
              }`}
            >
              Lũy kế theo lệnh
            </button>
          </div>

          {/* Quick Date Filters */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-[var(--color-text-muted)]">Khoảng nhanh:</span>
            <button
              type="button"
              onClick={handleFilterToday}
              className="rounded border border-[var(--color-border)] px-2.5 py-1 text-xs hover:bg-[var(--color-surface-hover)]"
            >
              Hôm nay
            </button>
            <button
              type="button"
              onClick={handleFilter7Days}
              className="rounded border border-[var(--color-border)] px-2.5 py-1 text-xs hover:bg-[var(--color-surface-hover)]"
            >
              7 ngày
            </button>
            <button
              type="button"
              onClick={handleFilterThisMonth}
              className="rounded border border-[var(--color-border)] px-2.5 py-1 text-xs hover:bg-[var(--color-surface-hover)]"
            >
              Tháng này
            </button>
          </div>
        </div>

        {/* Row 2: Date inputs & Dropdown filters */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 items-end">
          {/* From Date */}
          <div>
            <label htmlFor="from-date" className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
              Từ ngày
            </label>
            <DateInput
              id="from-date"
              value={from}
              onChange={(val) => {
                setFrom(val)
                setPage(1)
              }}
              className="w-full text-sm"
            />
          </div>

          {/* To Date */}
          <div>
            <label htmlFor="to-date" className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
              Đến ngày
            </label>
            <DateInput
              id="to-date"
              value={to}
              onChange={(val) => {
                setTo(val)
                setPage(1)
              }}
              className="w-full text-sm"
            />
          </div>

          {/* Group by (for view 1) */}
          <div>
            <label htmlFor="group-by" className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
              Xem theo kỳ
            </label>
            <select
              id="group-by"
              value={groupBy}
              onChange={(e) => {
                setGroupBy(e.target.value as GroupByPeriod)
                setPage(1)
              }}
              className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-1.5 text-sm"
            >
              <option value="day">Theo Ngày</option>
              <option value="week">Theo Tuần</option>
              <option value="month">Theo Tháng</option>
            </select>
          </div>

          {/* Plan Line selector */}
          <div>
            <label htmlFor="select-plan-line" className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
              Lệnh sản xuất
            </label>
            <select
              id="select-plan-line"
              value={selectedPlanLineId}
              onChange={(e) => {
                setSelectedPlanLineId(e.target.value)
                setPage(1)
              }}
              className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-1.5 text-sm truncate"
            >
              <option value="">Tất cả lệnh sản xuất</option>
              {data?.available_orders.map((ord) => (
                <option key={ord.plan_line_id} value={ord.plan_line_id}>
                  {ord.order_no} — {ord.product_code}
                </option>
              ))}
            </select>
          </div>

          {/* Product Code */}
          <div>
            <label htmlFor="input-product-code" className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
              Mã hàng
            </label>
            <input
              id="input-product-code"
              type="text"
              placeholder="VD: MP587..."
              value={productCodeInput}
              onChange={(e) => {
                setProductCodeInput(e.target.value)
                setPage(1)
              }}
              className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-1.5 text-sm"
            />
          </div>

          {/* Stage Filter */}
          <div>
            <label htmlFor="select-stage" className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
              Công đoạn
            </label>
            <select
              id="select-stage"
              value={selectedStage}
              onChange={(e) => {
                setSelectedStage(e.target.value as StageName | '')
                setPage(1)
              }}
              className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-1.5 text-sm"
            >
              <option value="">Tất cả 7 công đoạn</option>
              <option value="PHOI">1. Phôi</option>
              <option value="DINH_HINH">2. Định hình</option>
              <option value="LAP_RAP">3. Lắp ráp</option>
              <option value="CHA_NHAM">4. Chà nhám</option>
              <option value="SON">5. Sơn</option>
              <option value="FITTING">6. Fitting</option>
              <option value="DONG_GOI">7. Đóng gói</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      {loading && <PageLoading label="Đang tải dữ liệu báo cáo sản lượng..." />}

      {error && !loading && <PageError message={error} onRetry={() => void loadData()} />}

      {!loading && !error && isCurrentViewEmpty && (
        <EmptyState
          title="Chưa có sản lượng nào trong khoảng ngày này"
          description="Không tìm thấy bản ghi sản lượng 7 công đoạn nào phù hợp với bộ lọc ngày và điều kiện đã chọn."
          action={
            <Button variant="secondary" size="sm" onClick={handleFilterThisMonth}>
              Xem tháng này
            </Button>
          }
        />
      )}

      {!loading && !error && !isCurrentViewEmpty && (
        <div className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-level-1)] overflow-hidden">
          {/* VIEW 1: Theo mã hàng × công đoạn */}
          {activeView === 'by_period' && data && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-sm" data-testid="table-by-period">
                <thead>
                  {/* Super Header: Phân chia 2 khối đơn vị riêng biệt */}
                  <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface-hover)] text-xs font-semibold text-[var(--color-text-muted)]">
                    <th colSpan={6} className="px-3 py-2 border-r border-[var(--color-border)]">
                      THÔNG TIN ĐƠN HÀNG &amp; KỲ BÁO CÁO
                    </th>
                    <th
                      colSpan={2}
                      className="px-3 py-2 text-center border-r border-[var(--color-border)] bg-blue-50/50 text-blue-800 font-bold"
                    >
                      SẢN LƯỢNG CHI TIẾT (ĐVT: Chi tiết)
                    </th>
                    <th
                      colSpan={5}
                      className="px-3 py-2 text-center bg-emerald-50/50 text-emerald-800 font-bold"
                    >
                      SẢN LƯỢNG SẢN PHẨM HOÀN THIỆN (ĐVT: SP)
                    </th>
                  </tr>
                  {/* Detailed Header */}
                  <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface)] text-xs font-semibold text-[var(--color-text)]">
                    <th className="px-3 py-2 w-12 text-center">STT</th>
                    <th className="px-3 py-2">Kỳ</th>
                    <th className="px-3 py-2">Mã hàng</th>
                    <th className="px-3 py-2">Tên hàng</th>
                    <th className="px-3 py-2">Lệnh SX</th>
                    <th className="px-3 py-2 border-r border-[var(--color-border)]">Khách hàng</th>
                    {/* Khối chi tiết */}
                    <th className="px-3 py-2 text-right">Phôi</th>
                    <th className="px-3 py-2 text-right">Định hình</th>
                    {/* Khối sản phẩm */}
                    <th className="px-3 py-2 text-right">Lắp ráp</th>
                    <th className="px-3 py-2 text-right">Chà nhám</th>
                    <th className="px-3 py-2 text-right">Sơn</th>
                    <th className="px-3 py-2 text-right">Fitting</th>
                    <th className="px-3 py-2 text-right">Đóng gói</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                  {data.by_period.map((row, idx) => (
                    <tr key={`${row.period_key}-${row.plan_line_id}-${idx}`} className="hover:bg-[var(--color-surface-hover)]">
                      <td className="px-3 py-2 text-center text-xs text-[var(--color-text-muted)]">
                        {(data.pagination.page - 1) * data.pagination.limit + idx + 1}
                      </td>
                      <td className="px-3 py-2 font-medium">{row.period_label}</td>
                      <td className="px-3 py-2 font-mono font-semibold text-[var(--color-primary)]">
                        {row.product_code}
                      </td>
                      <td className="px-3 py-2 text-xs truncate max-w-[200px]" title={row.product_name}>
                        {row.product_name}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{row.order_no}</td>
                      <td className="px-3 py-2 text-xs border-r border-[var(--color-border)]">
                        {row.customer_name || '—'}
                      </td>
                      {/* Khối chi tiết */}
                      <td className="px-3 py-2 text-right">{formatNumber(row.phoi_qty)}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(row.dinh_hinh_qty)}</td>
                      {/* Khối SP */}
                      <td className="px-3 py-2 text-right">{formatNumber(row.lap_rap_qty)}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(row.cha_nham_qty)}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(row.son_qty)}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(row.fitting_qty)}</td>
                      <td className="px-3 py-2 text-right font-medium text-emerald-700">
                        {formatNumber(row.dong_goi_qty)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {/* Dòng tổng cộng mỗi khối riêng */}
                <tfoot>
                  <tr className="border-t-2 border-[var(--color-border)] bg-[var(--color-surface-hover)] font-bold text-xs">
                    <td colSpan={6} className="px-3 py-2.5 text-center border-r border-[var(--color-border)] uppercase">
                      Tổng cộng trong kỳ
                    </td>
                    <td className="px-3 py-2.5 text-right">{formatNumber(data.summary.total_phoi)}</td>
                    <td className="px-3 py-2.5 text-right">{formatNumber(data.summary.total_dinh_hinh)}</td>
                    <td className="px-3 py-2.5 text-right">{formatNumber(data.summary.total_lap_rap)}</td>
                    <td className="px-3 py-2.5 text-right">{formatNumber(data.summary.total_cha_nham)}</td>
                    <td className="px-3 py-2.5 text-right">{formatNumber(data.summary.total_son)}</td>
                    <td className="px-3 py-2.5 text-right">{formatNumber(data.summary.total_fitting)}</td>
                    <td className="px-3 py-2.5 text-right text-emerald-800">
                      {formatNumber(data.summary.total_dong_goi)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* VIEW 2: Theo chi tiết của một lệnh */}
          {activeView === 'order_parts' && data?.order_parts && (
            <div>
              {/* Header của Lệnh đang xem */}
              <div className="border-b border-[var(--color-border)] bg-[var(--color-surface-hover)] p-3 flex flex-wrap items-center justify-between gap-3 text-xs">
                <div className="flex flex-wrap items-center gap-4">
                  <div>
                    <span className="text-[var(--color-text-muted)]">Lệnh SX: </span>
                    <span className="font-mono font-bold text-[var(--color-text)]">
                      {data.order_parts.order_no}
                    </span>
                  </div>
                  <div>
                    <span className="text-[var(--color-text-muted)]">Mã hàng: </span>
                    <span className="font-semibold text-[var(--color-primary)]">
                      {data.order_parts.product_code}
                    </span>
                    <span className="ml-1 text-[var(--color-text-muted)]">({data.order_parts.product_name})</span>
                  </div>
                  <div>
                    <span className="text-[var(--color-text-muted)]">Khách hàng: </span>
                    <span className="font-medium">{data.order_parts.customer_name || '—'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="rounded bg-blue-50 px-2.5 py-1 text-blue-800 font-medium">
                    Kế hoạch lệnh: {formatNumber(data.order_parts.planned_qty)} SP
                  </div>
                  {data.order_parts.requested_ship_date && (
                    <div className="rounded bg-gray-100 px-2.5 py-1 text-gray-700">
                      Hạn giao: {formatDateFull(data.order_parts.requested_ship_date)}
                    </div>
                  )}
                </div>
              </div>

              {/* Table chi tiết */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left text-sm" data-testid="table-order-parts">
                  <thead>
                    <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface)] text-xs font-semibold text-[var(--color-text)]">
                      <th className="px-3 py-2 w-12 text-center">STT</th>
                      <th className="px-3 py-2">Tên chi tiết</th>
                      <th className="px-3 py-2">Quy cách (D×R×D mm)</th>
                      <th className="px-3 py-2">Loại gỗ</th>
                      <th className="px-3 py-2 text-right">Định mức/SP</th>
                      <th className="px-3 py-2 text-right border-r border-[var(--color-border)]">
                        Kế hoạch CT (chi tiết)
                      </th>
                      {/* Trong kỳ */}
                      <th className="px-3 py-2 text-right bg-blue-50/20">Phôi (trong kỳ)</th>
                      <th className="px-3 py-2 text-right bg-blue-50/20">Định hình (trong kỳ)</th>
                      <th className="px-3 py-2 text-right font-semibold border-r border-[var(--color-border)] bg-blue-50/40">
                        Tổng trong kỳ
                      </th>
                      {/* Lũy kế */}
                      <th className="px-3 py-2 text-right">Phôi (lũy kế)</th>
                      <th className="px-3 py-2 text-right">Định hình (lũy kế)</th>
                      <th className="px-3 py-2 text-right font-semibold border-r border-[var(--color-border)]">
                        Tổng lũy kế
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-amber-700">Tồn WIP chờ ráp</th>
                      <th className="px-3 py-2 text-right w-20">% Đạt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-border)]">
                    {data.order_parts.parts.map((p) => (
                      <tr key={p.part_snapshot_id} className="hover:bg-[var(--color-surface-hover)]">
                        <td className="px-3 py-2 text-center text-xs text-[var(--color-text-muted)]">{p.stt}</td>
                        <td className="px-3 py-2 font-medium">{p.part_name}</td>
                        <td className="px-3 py-2 font-mono text-xs text-[var(--color-text-muted)]">{p.spec}</td>
                        <td className="px-3 py-2 text-xs">{p.wood_type || '—'}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{p.qty_per}</td>
                        <td className="px-3 py-2 text-right font-semibold border-r border-[var(--color-border)]">
                          {formatNumber(p.target_qty)}
                        </td>
                        {/* Trong kỳ */}
                        <td className="px-3 py-2 text-right bg-blue-50/10">{formatNumber(p.phoi_in_period)}</td>
                        <td className="px-3 py-2 text-right bg-blue-50/10">{formatNumber(p.dinh_hinh_in_period)}</td>
                        <td className="px-3 py-2 text-right font-medium border-r border-[var(--color-border)] bg-blue-50/30">
                          {formatNumber(p.total_in_period)}
                        </td>
                        {/* Lũy kế */}
                        <td className="px-3 py-2 text-right">{formatNumber(p.cum_phoi)}</td>
                        <td className="px-3 py-2 text-right">{formatNumber(p.cum_dinh_hinh)}</td>
                        <td className="px-3 py-2 text-right font-medium border-r border-[var(--color-border)]">
                          {formatNumber(p.total_cum)}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-amber-700">
                          {formatNumber(p.wip_waiting_assembly)}
                        </td>
                        <td className="px-3 py-2 text-right text-xs font-semibold">
                          <span
                            className={
                              p.progress_pct >= 100
                                ? 'text-emerald-700'
                                : p.progress_pct > 0
                                ? 'text-blue-700'
                                : 'text-[var(--color-text-muted)]'
                            }
                          >
                            {p.progress_pct}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-[var(--color-border)] bg-[var(--color-surface-hover)] font-bold text-xs">
                      <td colSpan={4} className="px-3 py-2.5 text-center uppercase">
                        Tổng cộng chi tiết
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono">{data.order_parts.summary.total_qty_per}</td>
                      <td className="px-3 py-2.5 text-right border-r border-[var(--color-border)]">
                        {formatNumber(data.order_parts.summary.total_target_qty)}
                      </td>
                      <td className="px-3 py-2.5 text-right bg-blue-50/30">
                        {formatNumber(data.order_parts.summary.total_phoi_in_period)}
                      </td>
                      <td className="px-3 py-2.5 text-right bg-blue-50/30">
                        {formatNumber(data.order_parts.summary.total_dinh_hinh_in_period)}
                      </td>
                      <td className="px-3 py-2.5 text-right border-r border-[var(--color-border)] bg-blue-100/50 text-blue-900">
                        {formatNumber(
                          data.order_parts.summary.total_phoi_in_period +
                            data.order_parts.summary.total_dinh_hinh_in_period,
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {formatNumber(data.order_parts.summary.total_cum_phoi)}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {formatNumber(data.order_parts.summary.total_cum_dinh_hinh)}
                      </td>
                      <td className="px-3 py-2.5 text-right border-r border-[var(--color-border)] font-bold">
                        {formatNumber(
                          data.order_parts.summary.total_cum_phoi +
                            data.order_parts.summary.total_cum_dinh_hinh,
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right text-amber-800">
                        {formatNumber(data.order_parts.summary.total_wip_waiting)}
                      </td>
                      <td className="px-3 py-2.5 text-right"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* VIEW 3: Lũy kế theo lệnh */}
          {activeView === 'order_cumulative' && data && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-sm" data-testid="table-order-cumulative">
                <thead>
                  <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface-hover)] text-xs font-semibold text-[var(--color-text-muted)]">
                    <th colSpan={7} className="px-3 py-2 border-r border-[var(--color-border)]">
                      LỆNH SẢN XUẤT &amp; KẾ HOẠCH
                    </th>
                    <th
                      colSpan={2}
                      className="px-3 py-2 text-center border-r border-[var(--color-border)] bg-blue-50/50 text-blue-800 font-bold"
                    >
                      LŨY KẾ CHI TIẾT (ĐVT: Chi tiết)
                    </th>
                    <th
                      colSpan={5}
                      className="px-3 py-2 text-center border-r border-[var(--color-border)] bg-emerald-50/50 text-emerald-800 font-bold"
                    >
                      LŨY KẾ HOÀN THIỆN (ĐVT: SP)
                    </th>
                    <th colSpan={4} className="px-3 py-2 text-center">
                      TIẾN ĐỘ &amp; THỜI HẠN
                    </th>
                  </tr>
                  <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface)] text-xs font-semibold text-[var(--color-text)]">
                    <th className="px-3 py-2 w-12 text-center">STT</th>
                    <th className="px-3 py-2">Lệnh SX</th>
                    <th className="px-3 py-2">Mã hàng</th>
                    <th className="px-3 py-2">Tên hàng</th>
                    <th className="px-3 py-2">Khách hàng</th>
                    <th className="px-3 py-2 text-right">Kế hoạch (SP)</th>
                    <th className="px-3 py-2 border-r border-[var(--color-border)]">Hạn giao khách</th>
                    {/* Chi tiết */}
                    <th className="px-3 py-2 text-right">Phôi</th>
                    <th className="px-3 py-2 text-right">Định hình</th>
                    {/* SP */}
                    <th className="px-3 py-2 text-right">Lắp ráp</th>
                    <th className="px-3 py-2 text-right">Chà nhám</th>
                    <th className="px-3 py-2 text-right">Sơn</th>
                    <th className="px-3 py-2 text-right">Fitting</th>
                    <th className="px-3 py-2 text-right text-emerald-700">Đóng gói</th>
                    {/* Tiến độ */}
                    <th className="px-3 py-2 text-right font-medium text-amber-700">Còn lại (SP)</th>
                    <th className="px-3 py-2 text-right w-16">% HT</th>
                    <th className="px-3 py-2 text-center">Ngày làm cuối</th>
                    <th className="px-3 py-2 text-center">Tình trạng</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                  {data.order_cumulative.map((row) => (
                    <tr key={row.plan_line_id} className="hover:bg-[var(--color-surface-hover)]">
                      <td className="px-3 py-2 text-center text-xs text-[var(--color-text-muted)]">{row.stt}</td>
                      <td className="px-3 py-2 font-mono font-medium">{row.order_no}</td>
                      <td className="px-3 py-2 font-mono font-semibold text-[var(--color-primary)]">
                        {row.product_code}
                      </td>
                      <td className="px-3 py-2 text-xs truncate max-w-[180px]" title={row.product_name}>
                        {row.product_name}
                      </td>
                      <td className="px-3 py-2 text-xs">{row.customer_name || '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold">{formatNumber(row.planned_qty)}</td>
                      <td className="px-3 py-2 text-xs border-r border-[var(--color-border)]">
                        {row.requested_ship_date ? formatDateFull(row.requested_ship_date) : '—'}
                      </td>
                      {/* Chi tiết */}
                      <td className="px-3 py-2 text-right">{formatNumber(row.cum_phoi)}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(row.cum_dinh_hinh)}</td>
                      {/* SP */}
                      <td className="px-3 py-2 text-right">{formatNumber(row.cum_lap_rap)}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(row.cum_cha_nham)}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(row.cum_son)}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(row.cum_fitting)}</td>
                      <td className="px-3 py-2 text-right font-medium text-emerald-700">
                        {formatNumber(row.cum_dong_goi)}
                      </td>
                      {/* Còn lại & % */}
                      <td className="px-3 py-2 text-right font-semibold text-amber-700">
                        {formatNumber(row.remaining_qty)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-xs">
                        <span
                          className={
                            row.progress_pct >= 100
                              ? 'text-emerald-700'
                              : row.progress_pct > 0
                              ? 'text-blue-700'
                              : 'text-[var(--color-text-muted)]'
                          }
                        >
                          {row.progress_pct}%
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center text-xs text-[var(--color-text-muted)]">
                        {row.latest_event_date || '—'}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <StatusBadge
                          label={row.ship_status.label}
                          tone={
                            row.ship_status.code === 'done'
                              ? 'success'
                              : row.ship_status.code === 'overdue'
                              ? 'danger'
                              : row.ship_status.code === 'at_risk'
                              ? 'warning'
                              : 'neutral'
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination bar */}
          {data && data.pagination.total_pages > 1 && (
            <div className="flex items-center justify-between border-t border-[var(--color-border)] px-4 py-3 bg-[var(--color-surface)] text-xs text-[var(--color-text-muted)]">
              <div>
                Hiển thị trang <span className="font-semibold text-[var(--color-text)]">{data.pagination.page}</span> /{' '}
                <span className="font-semibold text-[var(--color-text)]">{data.pagination.total_pages}</span> (Tổng{' '}
                {data.pagination.total} dòng)
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={data.pagination.page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Trước
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(data.pagination.total_pages, p + 1))}
                  disabled={data.pagination.page >= data.pagination.total_pages}
                >
                  Sau
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
